import { getConfig } from './secureConfig';
import secureLog from './secureLogger';

// Add this helper at the top of the file
const isBrowser = typeof window !== 'undefined';

/**
 * Securely calls a webhook without exposing the URL in logs
 * @param {Object} userData - User data to send to the webhook
 * @returns {Promise} - Promise that resolves with the response
 */
export const callSignupWebhook = async (userData) => {
  try {
    // Only call webhook if we have user data and it's a signup operation
    if (!userData || !userData.email) {
      secureLog.debug('Skipping webhook call - no user data provided');
      return { success: true, skipped: true };
    }
    
    // Skip webhook calls on server-side rendering
    if (!isBrowser) {
      secureLog.debug('Skipping webhook call - server-side rendering');
      return { success: true, skipped: true };
    }
    
    // Check if this is a signup context - only call during signup
    const isSignupContext = 
      userData.metadata?.source === 'signup_form' || 
      window.location.pathname.includes('/getstarted');
      
    // Additional check to prevent webhook calls on listing pages
    const isListingPage = 
      window.location.pathname.includes('/l/') || 
      window.location.pathname.includes('/listing/');
      
    if (!isSignupContext || isListingPage) {
      secureLog.debug('Skipping webhook call - not in signup context or on listing page', {
        path: window.location.pathname,
        isSignupContext,
        isListingPage
      });
      return { success: true, skipped: true };
    }
    
    // Add fallback URL for production
    const webhookUrl = process.env.REACT_APP_SHARETRIBE_WEBHOOK_URL || 
      'https://obvodmlgphlgqatvmafe.supabase.co/functions/v1/sharetribe-webhook';      

    secureLog.debug('Calling signup webhook', {
      userType: userData?.publicData?.userType || 'unknown',
      hasWebhookUrl: !!webhookUrl
    });

    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    secureLog.debug('Webhook response received', {
      status: response.status,
      ok: response.ok,
      success: response.ok
    });

    return { success: response.ok, status: response.status };
  } catch (error) {
    secureLog.error('Webhook call failed', { message: error.message });
    // Return success to prevent breaking the app flow
    return { success: false, error: error.message };
  }
}; 